const formatScenarioLabel = (val) => {
  if (!val) return null;
  const normalized = String(val).trim();
  if (!normalized) return null;
  return normalized
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getScenarioSectionHeading = (optimizationData) => {
  const meta =
    optimizationData?.process_type || optimizationData?.processType || null;
  const metaType = String(meta?.type || "").toLowerCase();
  const hasScenario = Boolean(
    (metaType === "insurance" &&
      (meta?.scenario_type || meta?.scenarioType)) ||
      optimizationData?.scenario_type ||
      optimizationData?.scenarioType,
  );
  if (hasScenario) return "Scenario Type";
  if (meta?.type || optimizationData?.type) return "Process Type";
  if (optimizationData?.process_category || optimizationData?.processCategory)
    return "Process Category";
  return "Scenario Type";
};

export const getScenarioTypeLabel = (optimizationData) => {
  const meta =
    optimizationData?.process_type || optimizationData?.processType || null;
  const metaType = String(meta?.type || "").toLowerCase();
  const scenarioFromMeta =
    metaType === "insurance" ?
      (meta?.scenario_type ?? meta?.scenarioType)
    : null;

  const firstNonNull = [
    scenarioFromMeta,
    optimizationData?.scenario_type,
    optimizationData?.scenarioType,
    meta?.type,
    optimizationData?.type,
    optimizationData?.process_category,
    optimizationData?.processCategory,
    optimizationData?.process_name,
  ].find((item) => Boolean(formatScenarioLabel(item)));

  return formatScenarioLabel(firstNonNull) || "N/A";
};

export const buildVersionPayload = (data) => {
  if (!data || typeof data !== "object") return {};
  const workflow =
    Array.isArray(data.workflow) ?
      data.workflow
        .map((w, idx) => {
          const rawType = w.item_type || w.type || w.itemType;
          const normalizedType = rawType ? String(rawType).toLowerCase() : null;
          const taskId =
            w.task_id != null ? Number(w.task_id)
            : w.taskId != null ? Number(w.taskId)
            : undefined;
          const processId =
            w.process_id != null ? Number(w.process_id)
            : w.processId != null ? Number(w.processId)
            : undefined;
          const sequence =
            w.sequence_number != null ? Number(w.sequence_number)
            : w.sequenceNumber != null ? Number(w.sequenceNumber)
            : idx + 1;

          // A SUB-PROCESS slot must be saved as `child_process_id` (the CMS workflow
          // contract: task_id and child_process_id are mutually exclusive). The agent
          // also sets task_id = the child process id for the diagram renderer, so it
          // must NOT be forwarded as a task_id here — that would save a task reference
          // pointing at a process id.
          const childProcessId =
            w.child_process_id != null ? Number(w.child_process_id)
            : w.childProcessId != null ? Number(w.childProcessId)
            : normalizedType && /sub[_-]?process/.test(normalizedType) ? taskId
            : undefined;
          const isSubProcess = childProcessId != null;

          if (isSubProcess) {
            return {
              item_type: "process",
              child_process_id: childProcessId,
              order: sequence,
              sequence_number: sequence,
            };
          }

          return {
            item_type:
              normalizedType ||
              (taskId != null ? "task"
              : processId != null ? "process"
              : "task"),
            task_id: taskId,
            process_id: processId,
            order: sequence,
            sequence_number: sequence,
          };
        })
        .filter(
          (item) =>
            item.task_id != null ||
            item.process_id != null ||
            item.child_process_id != null,
        )
    : [];

  const gateways =
    Array.isArray(data.gateways) ?
      data.gateways.map((g) => ({
        gateway_type: (
          g.gateway_type ||
          g.type ||
          g.gatewayType ||
          ""
        ).toLowerCase(),
        name: g.name || "",
        after_task_id:
          g.after_task_id != null ? Number(g.after_task_id)
          : g.afterTaskId != null ? Number(g.afterTaskId)
          : null,
        // Converge/join data and gateway↔gateway links are part of the stored
        // Ch.3 structure — dropping them here silently flattened every saved
        // version's joins.
        converge_at_task_id:
          g.converge_at_task_id != null ? Number(g.converge_at_task_id)
          : g.convergeAtTaskId != null ? Number(g.convergeAtTaskId)
          : undefined,
        converge_gateway_name: g.converge_gateway_name ?? g.convergeGatewayName,
        converge_to_end: g.converge_to_end ?? g.convergeToEnd,
        after_gateway_index: g.after_gateway_index ?? g.afterGatewayIndex,
        converge_at_gateway_index:
          g.converge_at_gateway_index ?? g.convergeAtGatewayIndex,
        branches:
          Array.isArray(g.branches) ?
            g.branches.map((b) => ({
              condition: b.condition,
              is_default: b.is_default ?? b.isDefault ?? false,
              target_task_id:
                b.target_task_id != null ? Number(b.target_task_id)
                : b.targetTaskId != null ? Number(b.targetTaskId)
                : undefined,
              end_event_name: b.end_event_name || b.endEventName,
              end_task_id:
                b.end_task_id != null ? Number(b.end_task_id)
                : b.endTaskId != null ? Number(b.endTaskId)
                : undefined,
              connect_to_end: b.connect_to_end ?? b.connectToEnd,
              target_gateway_index:
                b.target_gateway_index ?? b.targetGatewayIndex,
              // FBPM Ch.7 XOR/OR weighting — analyst-owned measured data. Losing
              // it reverted the saved version's flow analysis to equal-split.
              probability:
                b.probability != null ? Number(b.probability) : undefined,
            }))
          : [],
      }))
    : [];

  return {
    process_name: data.process_name || data.name,
    process_code: data.process_code || data.code,
    process_overview: data.process_overview || data.overview,
    capacity_requirement_minutes: data.capacity_requirement_minutes,
    process_status_id: data.process_status_id,
    process_category_id: data.process_category_id,
    workflow,
    gateways,
  };
};
